/**
 * Tenant hostname helpers for *.cheritto.com hospital URLs.
 * Local development also accepts {slug}.localhost (never used in production).
 */

const DEFAULT_BASE_DOMAIN = "cheritto.com";
const LOCAL_DEV_BASE_DOMAIN = "localhost";

const GENERIC_LABELS = new Set(["app", "uat-app", "www", "localhost"]);

function resolveBaseDomain(hostname) {
  const normalized = normalizeHost(hostname)
    || (typeof window !== "undefined" ? String(window.location.hostname || "").toLowerCase() : "")
    || "";
  if (normalized === LOCAL_DEV_BASE_DOMAIN || normalized.endsWith(`.${LOCAL_DEV_BASE_DOMAIN}`)) {
    return LOCAL_DEV_BASE_DOMAIN;
  }
  const fromEnv =
    typeof window !== "undefined"
      ? window._env_?.TENANT_BASE_DOMAIN || window._env_?.REACT_APP_TENANT_BASE_DOMAIN
      : null;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().toLowerCase();
  }
  if (normalized.endsWith(".cheritto.com") || normalized === "cheritto.com") {
    return "cheritto.com";
  }
  return DEFAULT_BASE_DOMAIN;
}

export function getTenantBaseDomain() {
  return resolveBaseDomain();
}

export function normalizeHost(hostname) {
  if (!hostname) return null;
  let host = String(hostname).trim().toLowerCase();
  if (host.startsWith("http://") || host.startsWith("https://")) {
    try {
      host = new URL(host).hostname;
    } catch {
      return null;
    }
  }
  const colon = host.lastIndexOf(":");
  if (colon > 0 && /^\d+$/.test(host.slice(colon + 1))) {
    host = host.slice(0, colon);
  }
  return host || null;
}

/**
 * Extract tenant slug from hostname, or null for generic/platform hosts.
 */
export function extractTenantSlugFromHostname(hostname = window.location.hostname) {
  const host = normalizeHost(hostname);
  const base = resolveBaseDomain(host);
  if (!host || !base) return null;
  if (host === base) return null;
  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return null;
  const label = host.slice(0, -suffix.length);
  if (!label || label.includes(".")) return null;
  if (GENERIC_LABELS.has(label)) return null;
  return label;
}

export function isGenericAppHost(hostname = window.location.hostname) {
  const host = normalizeHost(hostname);
  const base = resolveBaseDomain(host);
  if (!host || !base) return true;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.endsWith(".localhost")) {
    const label = host.slice(0, -".localhost".length);
    return !label || GENERIC_LABELS.has(label) || label.includes(".");
  }
  if (!host.endsWith(`.${base}`) && host !== base) return true;
  const label = host.endsWith(`.${base}`)
    ? host.slice(0, -(base.length + 1))
    : null;
  return !label || GENERIC_LABELS.has(label) || label.includes(".");
}

export function buildTenantPublicUrl(slug) {
  if (!slug) return null;
  const scheme = window.location.protocol === "http:" ? "http" : "https";
  return `${scheme}://${String(slug).trim().toLowerCase()}.${resolveBaseDomain()}`;
}

const STORAGE_KEY = "tenantContext";

export function persistTenantContext(ctx) {
  if (!ctx) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      tenantSlug: ctx.tenantSlug,
      appsCode: ctx.appsCode,
      hospitalName: ctx.hospitalName,
      tenantUrl: ctx.tenantUrl,
      logoUrl: ctx.logoUrl || null,
      faviconUrl: ctx.faviconUrl || null,
      hasLogo: Boolean(ctx.hasLogo),
      hasFavicon: Boolean(ctx.hasFavicon),
      theme: ctx.theme || null,
    }),
  );
}

export function readTenantContext() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearTenantContext() {
  localStorage.removeItem(STORAGE_KEY);
}
